import { inject, NgModule } from '@angular/core';
import { Apollo, provideNamedApollo } from 'apollo-angular';
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache } from '@apollo/client';
import { HttpLink } from 'apollo-angular/http';
import { EnvironmentService, LipoMessageService } from 'FoxxyLib';
import { IClientError, ISortimentierenEnvironment } from 'FoxxyInterfaces';
import { ErrorLink } from '@apollo/client/link/error';
import { Store } from '@ngrx/store';
import { LogUsersClientErrorAction } from './store/user/actions';
import { LogLevel } from '@microsoft/signalr';
import { MessageService, ToastMessageOptions } from 'primeng/api';

import { GraphQLService } from './graphql.service';
import { CockpitStore } from './cockpit/store/cockpitStore';

// https://stackoverflow.com/questions/57524580/how-to-use-import-onerror-from-apollo-link-error-in-angular-to-handle-all

/**#8843: Apollo Client 4 verlangt, die Standard-errorPolicy beider Clients (unten in defaultOptions) für die
 * Typen zu deklarieren – Ergebnisse tragen GraphQL-Fehler dann in `result.error`, `data` kann unvollständig sein. */
declare module '@apollo/client' {
  export namespace ApolloClient {
    export namespace DeclareDefaultOptions {
      interface Query {
        errorPolicy: 'all';
      }
      interface WatchQuery {
        errorPolicy: 'all';
      }
    }
  }
}




/**Errorhandling: GraphQL-Fehler nur protokollieren (die Aufrufer zeigen sie an), alle anderen Fehler
 * (Netzwerk, Server) zusätzlich als Toast. #8843: Apollo Client 4 liefert beides als ein `error`. */
export function createErrorLink(store: Store<any>, mess: LipoMessageService<ToastMessageOptions>): ApolloLink {

  return new ErrorLink(({ error }) => {

    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path, extensions }) => {
        const e = `[GraphQL error]: Message: ${message}, Location: ${locations?.toString()}, Path: ${path?.toString()}, Extensions: ${extensions ? JSON.stringify(extensions) : ''}  `;

        store.dispatch(new LogUsersClientErrorAction({ error: <IClientError>{ loglevel: LogLevel.Debug, userid: null, error: e, modul: 'GraphQl Sortimentieren' } }));
      });
    } else {
      mess.Publish({ severity: 'error', summary: 'Network Error', detail: error.message });
      store.dispatch(new LogUsersClientErrorAction({ error: <IClientError>{ loglevel: LogLevel.Error, userid: null, error: error, modul: 'GraphQl Sortimentieren' } }));
    }
  });
}



const graphqlMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      'Access-Control-Allow-Origin': '*',
    }
  });
  return forward(operation);
})

/**Test Apollo von Roland*/
export function createApolloTest(httpLink: HttpLink): ApolloClient.Options {

  const urlTest = "https://parfesdat02.paragon-systems.de:7150/graphql/";
  const store = inject(Store);
  const lipomessageService = inject(LipoMessageService<ToastMessageOptions>)
  //console.log("createApollo Roland TEST URL:" + urlTest);
  return {
    link: ApolloLink.from([
      createErrorLink(store, lipomessageService),
      graphqlMiddleware,
      httpLink.create({
        uri: (operation) => {
          const opName = operation.operationName
            || operation.query?.definitions?.[0]?.['name']?.value
            || 'query';
          return `${urlTest}?op=${opName}`;
        }
      })
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      },
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      }
    }
  };
}

/**Apollo Sortimentieren Endpunkt
 * _typnames raus  --> https://stackoverflow.com/questions/47211778/cleaning-unwanted-fields-from-graphql-responses/51380645#51380645
*/
export function createApolloSortimentieren(httpLink: HttpLink): ApolloClient.Options {
  const envir = inject(EnvironmentService<ISortimentierenEnvironment>) as EnvironmentService<ISortimentierenEnvironment>;
  const store = inject(Store);
  const lipomessageService = inject(LipoMessageService<ToastMessageOptions>)
  const baseUrl = envir.environment.graphQLUrlSortimentieren;

  return {
    link: ApolloLink.from([
      createErrorLink(store, lipomessageService),
      graphqlMiddleware,
      httpLink.create({
        uri: (operation) => {
          const opName = operation.operationName
            || operation.query?.definitions?.[0]?.['name']?.value
            || 'query';
          return `${baseUrl}?op=${opName}`;
        }
      })
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      },
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      }
    }
  };
}

@NgModule({
  providers: [Apollo, GraphQLService,CockpitStore,
    MessageService, LipoMessageService<ToastMessageOptions>,
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);
      return {
        default: createApolloSortimentieren(httpLink),
        test: createApolloTest(httpLink)
      }
    }),
  ],
})
export class GraphQLModule { }

